/**
 * useMetadataPolling Hook
 * Polls for pending metadata updates and returns updated links
 */

import { useEffect, useRef, useCallback } from "react";
import { Effect } from "effect";
import { getLinkById } from "../services/linkService";
import type { Link } from "../types/link";

interface PollingState {
  attempts: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
}

const MAX_ATTEMPTS = 5;
const POLL_INTERVALS = [1000, 2000, 3000, 4000, 5000]; // Progressive backoff

/**
 * Hook to poll for metadata updates on pending links
 * @param links - Current list of links
 * @param onUpdate - Callback when a link is updated
 */
export function useMetadataPolling(
  links: Link[],
  onUpdate: (updatedLink: Link) => void
) {
  const pollingStates = useRef<Map<string, PollingState>>(new Map());
  const onUpdateRef = useRef(onUpdate);

  // Keep onUpdate ref current without triggering re-renders
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const pollLink = useCallback(async (linkId: string) => {
    const state = pollingStates.current.get(linkId);
    if (!state || state.attempts >= MAX_ATTEMPTS) {
      pollingStates.current.delete(linkId);
      return;
    }

    const program = getLinkById(linkId);
    const result = await Effect.runPromise(Effect.either(program));

    if (result._tag === "Right") {
      const updatedLink = result.right as unknown as Link;
      const status = updatedLink.ogMetadata?.status;

      // Stop polling if metadata fetch completed (success or failed)
      if (status === "success" || status === "failed") {
        pollingStates.current.delete(linkId);
        onUpdateRef.current(updatedLink);
        return;
      }

      // Still pending, schedule next poll
      const nextAttempt = state.attempts + 1;
      if (nextAttempt < MAX_ATTEMPTS) {
        const timeoutId = setTimeout(
          () => pollLink(linkId),
          POLL_INTERVALS[nextAttempt]
        );
        pollingStates.current.set(linkId, {
          attempts: nextAttempt,
          timeoutId,
        });
      } else {
        // Max attempts reached, stop polling
        pollingStates.current.delete(linkId);
      }
    } else {
      // Error occurred, stop polling for this link
      console.error(`Failed to poll link ${linkId}:`, result.left);
      pollingStates.current.delete(linkId);
    }
  }, []);

  // Start polling for new pending links
  useEffect(() => {
    links.forEach((link) => {
      const isPending = link.ogMetadata?.status === "pending";
      const isAlreadyPolling = pollingStates.current.has(link.id);

      if (isPending && !isAlreadyPolling) {
        // Start polling for this link
        const timeoutId = setTimeout(
          () => pollLink(link.id),
          POLL_INTERVALS[0]
        );
        pollingStates.current.set(link.id, {
          attempts: 0,
          timeoutId,
        });
      }
    });
  }, [links, pollLink]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      pollingStates.current.forEach((state) => {
        if (state.timeoutId) {
          clearTimeout(state.timeoutId);
        }
      });
      pollingStates.current.clear();
    };
  }, []);
}
